<script setup lang="ts">
    import { defaultAutocompleteConverter } from "@/components/forms/inputs/Autocomplete/helpers";
    import { useStore } from "@/store";
    import { flashWarning } from "@/utils";
    import { ref, toRefs } from "vue";

    interface Props {
        name?: string;
        border?: boolean;
        variant?: "blue-background" | "transparent";
        /**
         * Ska options hämtas direkt vid mount?
         */
        retrieveOptionsOnMount?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        name: "typoperation",
        border: false,
        variant: "blue-background",
    });

    const { name } = toRefs(props);

    const store = useStore();

    const kAutocomplete = ref();
    const kontaktorsak = ref();

    const fetchTypoperation = async ({ query }: { query?: string } = {}) => {
        try {
            const params: { q?: string; kontaktorsak?: string } = {};

            if (query) {
                params.q = query;
            }

            if (kontaktorsak.value) {
                params.kontaktorsak = kontaktorsak.value;
            }

            const { subsetName } = await store.dispatch("operation/typoperation/fetchTypoperationer", {
                filterParams: params,
                displayMode: true,
            });

            return store.getters["operation/typoperation/typoperationerSubset"]({ subsetName, displayMode: true });
        } catch (error) {
            flashWarning("Kunde inte hämta typoperationer");
        }
    };

    defineExpose({
        formListeners: [
            {
                fieldNames: ["kontaktorsak"],
                runAtInitial: true,
                alwaysRunAtInitial: true,
                action: async ({ values }: { values: { kontaktorsak: any } }) => {
                    kontaktorsak.value =
                        typeof values.kontaktorsak === "object" ? values.kontaktorsak?.value : values.kontaktorsak;
                    kAutocomplete?.value?.setOptions({
                        options: await fetchTypoperation(),
                        setDefault: true,
                    });
                },
            },
        ],
    });
</script>

<template>
    <KAutocomplete
        :name="name"
        label="Typoperation"
        v-bind="$attrs"
        ref="kAutocomplete"
        :options="retrieveOptionsOnMount ? fetchTypoperation : undefined"
        :onSearch="fetchTypoperation"
        :converter="defaultAutocompleteConverter"
        :class="[{ 'field-container': border, [variant]: border }]"
    />
</template>

<style lang="scss" scoped>
    @import "@/components/forms/Form/styles/FieldBackgrounds";

    .field-container {
        @include field-container-mixin();
    }
</style>
